/**
 * Shared eligibility + launch contract for the interactive help guide.
 *
 * The trigger lives in the nav sidebar (NavigationV2) while the guide itself is
 * mounted at the app root (App.jsx), so two unrelated component trees have to
 * agree on two things:
 *
 *  1. **Who gets help at all.** Defined once here so the nav item and the guide
 *     can't drift apart — a nav entry that opens nothing, or a guide with no way
 *     to launch it, is exactly the failure this prevents.
 *
 *  2. **How to launch it.** A window CustomEvent, matching the pattern already
 *     used for cross-cutting signals in this app (`session:updated` in
 *     services/session.js, `palcare:transaction` in services/api.js). No context
 *     provider and no prop drilling through the whole nav tree.
 */

import { getRoleWeight, getCampusId, getCampusConfig, isResidentDataEhrManaged } from '../../services/session.js';
import { getAvailableFlows } from '../../data/helpFlows/index.js';

/** Caregivers (weight 10) can't perform any guided task, so they get no help. */
export const MIN_ROLE_WEIGHT = 10;

/** Fired at `window` to ask the guide to open its task picker. */
export const HELP_OPEN_EVENT = 'palcare:help-open';

/**
 * Fired at `window` whenever the guide starts or stops being in use — the picker
 * opening or closing, a flow starting or ending.
 *
 * The nav trigger lights up while help is active, and the nav lives in a
 * different tree from the guide that owns that state, so it travels the same way
 * the open request does rather than through a context provider.
 * `detail: { active: boolean }`.
 */
export const HELP_ACTIVE_EVENT = 'palcare:help-active';

/**
 * Is an EHR integration driving resident lifecycle for this campus?
 *
 * Delegates to the same helper the residents list and profile pages gate their
 * edit rights on, so a flow is never offered for an action the page will refuse.
 * That alignment is the whole point — three separate copies of this test is how
 * the list page ended up with no gate at all.
 */
function resolveEhrActive(campusId) {
    try {
        return isResidentDataEhrManaged(campusId);
    } catch {
        // A campus without EHR config must not break the picker.
        return false;
    }
}

/** The context each flow's `available` predicate is evaluated against. */
export function buildHelpContext(portalVersion) {
    const campusId = getCampusId();
    return {
        roleWeight: getRoleWeight(),
        campusConfig: getCampusConfig(campusId),
        ehrActive: resolveEhrActive(campusId),
        portalVersion,
    };
}

/**
 * Should help be offered at all?
 *
 * getRoleWeight (not getMyRoleWeight) means impersonation is respected: an admin
 * impersonating a caregiver also loses the help entry, which is correct — the
 * guided actions would be refused at that weight anyway.
 */
export function isHelpAvailable(portalVersion = 'v2') {
    if (portalVersion !== 'v2') return false;
    const ctx = buildHelpContext(portalVersion);
    if (ctx.roleWeight <= MIN_ROLE_WEIGHT) return false;
    return getAvailableFlows(ctx).length > 0;
}

/** Ask the guide to open its task picker. */
export function openHelpPicker() {
    window.dispatchEvent(new CustomEvent(HELP_OPEN_EVENT));
}

/** Announce that the guide is (or is no longer) in use. Called only by HelpGuide. */
export function emitHelpActive(active) {
    window.dispatchEvent(new CustomEvent(HELP_ACTIVE_EVENT, {
        detail: { active: Boolean(active) },
    }));
}
